import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelStorageRequest, newStorageRequestId } from '../../services/azureStorageApi';

export type SingleRequestOutcome<T> = { current: boolean; value?: T; error?: unknown; started: boolean };

/**
 * One outstanding request per dialog (review C N2): a second submission while one is pending is ignored (and the
 * submit button disabled through `pending`), so Close / unmount always cancels the only request there is.
 * `current` is false when the dialog closed or unmounted before the answer arrived.
 */
export function useSingleStorageRequest() {
  const active = useRef<{ id: string; cancel: () => Promise<void> } | null>(null);
  const mounted = useRef(true);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; void active.current?.cancel(); };
  }, []);
  const run = useCallback(async <T,>(invoke: (requestId: string) => Promise<T>): Promise<SingleRequestOutcome<T>> => {
    if (active.current) return { current: false, started: false };
    const requestId = newStorageRequestId();
    let cancelled = false;
    const cancel = async () => { if (!cancelled) { cancelled = true; await cancelStorageRequest(requestId); } };
    active.current = { id: requestId, cancel };
    setPending(true);
    const current = () => mounted.current && active.current?.id === requestId && !cancelled;
    try {
      const value = await invoke(requestId);
      return { current: current(), value, started: true };
    } catch (error) {
      return { current: current(), error, started: true };
    } finally {
      if (active.current?.id === requestId) active.current = null;
      if (mounted.current) setPending(false);
    }
  }, []);
  const cancel = useCallback(() => { void active.current?.cancel(); }, []);
  return { pending, run, cancel };
}
